/**
 * How a failed Benny turn presents itself, shared by the docked chat and the
 * Conversations thread so an error reads the same on both surfaces.
 *
 * `canRetry` is the important bit: an unauthorized or missing model can't be
 * retried into working, so offering "Try again" there just walks the user
 * into the same wall. Those cases get the Help Center as the way forward.
 */

import type { AssistantChatMessage } from '@scrambl/shared'

export interface ErrorPresentation {
  title: string
  canRetry: boolean
}

export const ERROR_PRESENTATION: Record<string, ErrorPresentation> = {
  BENNY_PROVIDER_UNAVAILABLE: {
    title: 'Benny is unavailable',
    canRetry: false,
  },
  BENNY_RATE_LIMITED: { title: 'Benny is rate limited', canRetry: true },
  BENNY_PROVIDER_TIMEOUT: { title: 'Benny timed out', canRetry: true },
  BENNY_PROVIDER_ERROR: {
    title: "Benny couldn't answer that",
    canRetry: true,
  },
  BENNY_TOOL_LOOP_EXHAUSTED: {
    title: 'Benny got stuck on that one',
    canRetry: true,
  },
  BENNY_TURN_TIMEOUT: { title: 'Benny ran out of time', canRetry: true },
}

const DEFAULT_PRESENTATION: ErrorPresentation = {
  title: "Benny couldn't answer that",
  canRetry: true,
}

/** The error code a failed turn was stamped with, if any. */
export function getErrorCode(
  message: Pick<AssistantChatMessage, 'metadata'>,
): string | undefined {
  const code = message.metadata?.errorCode
  return typeof code === 'string' ? code : undefined
}

export function getErrorPresentation(
  message: Pick<AssistantChatMessage, 'metadata'>,
): ErrorPresentation {
  const code = getErrorCode(message)
  return (code && ERROR_PRESENTATION[code]) || DEFAULT_PRESENTATION
}

/** True when the turn failed, whether or not partial content was kept. */
export function isErrorMessage(
  message: Pick<AssistantChatMessage, 'metadata'>,
): boolean {
  return getErrorCode(message) !== undefined
}
